declare global {
  interface Window {
    dataLayer: Record<string, unknown>[]
    clarity?: (...args: unknown[]) => void
  }
}

export type GA4EcommerceItem = {
  item_id: string
  item_name: string
  price: number
  quantity: number
  item_category?: string
  item_variant?: string
}

type GA4EcommerceEvent =
  | 'purchase'
  | 'add_to_cart'
  | 'remove_from_cart'
  | 'view_cart'
  | 'begin_checkout'
  | 'view_item'
  | 'view_item_list'
  | 'select_item'

type GA4EcommercePayload = {
  currency?: string
  value?: number
  transaction_id?: string
  items?: GA4EcommerceItem[]
  [key: string]: unknown
}

const DEFAULT_CURRENCY = 'MDL'

// Skip GA4/GTM dataLayer pushes on localhost so dev + Playwright e2e
// never pollute production stats. index.html applies the same gate to
// the script tags themselves.
function isAnalyticsDisabled(): boolean {
  if (typeof window === 'undefined') return true
  const h = window.location.hostname
  return h === 'localhost' || h === '127.0.0.1' || h === '0.0.0.0'
}

function trackEcommerce(
  eventName: GA4EcommerceEvent,
  payload: GA4EcommercePayload
): void {
  if (isAnalyticsDisabled()) return
  try {
    window.dataLayer = window.dataLayer || []
    // GA4 requires clearing the ecommerce object before each push so items
    // from prior events don't leak into the next one.
    window.dataLayer.push({ ecommerce: null })
    window.dataLayer.push({ event: eventName, ecommerce: payload })
  } catch (err) {
    console.error('[analytics] trackEcommerce failed:', err)
  }
}

function trackEvent(
  eventName: string,
  params: Record<string, unknown> = {}
): void {
  if (isAnalyticsDisabled()) return
  try {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({ event: eventName, ...params })
  } catch (err) {
    console.error('[analytics] trackEvent failed:', err)
  }
}

export function trackThreeDPerfSample(params: {
  fpsBucket: 'low' | 'med' | 'high'
  fps: number
  gpuTier: 'low' | 'medium' | 'high'
  worstFrameMs?: number
}): void {
  if (isAnalyticsDisabled()) return
  const payload: Record<string, unknown> = {
    fps_bucket: params.fpsBucket,
    fps: Math.round(params.fps),
    gpu_tier: params.gpuTier,
  }
  if (params.worstFrameMs !== undefined) {
    payload.worst_frame_ms = Math.round(params.worstFrameMs * 10) / 10
  }
  trackEvent('3d_perf_sample', payload)
  try {
    window.clarity?.('set', '3d_fps_bucket', params.fpsBucket)
    window.clarity?.('set', '3d_gpu_tier', params.gpuTier)
  } catch (err) {
    console.error('[analytics] clarity set failed:', err)
  }
}

export function trackThreeDPerfSession(params: {
  minFps: number
  avgFps: number
  dominantBucket: 'low' | 'med' | 'high'
  lowFrames: number
  totalFrames: number
  gpuTier: 'low' | 'medium' | 'high'
  url: string
}): void {
  if (isAnalyticsDisabled()) return
  const lowFramesPct =
    params.totalFrames > 0
      ? (params.lowFrames / params.totalFrames) * 100
      : 0
  trackEvent('3d_perf_session', {
    min_fps: Math.round(params.minFps),
    avg_fps: Math.round(params.avgFps),
    dominant_bucket: params.dominantBucket,
    low_frames_pct: Math.round(lowFramesPct * 10) / 10,
    total_frames: params.totalFrames,
    gpu_tier: params.gpuTier,
    page_path: params.url,
  })
  try {
    window.clarity?.(
      'set',
      '3d_session_dominant_bucket',
      params.dominantBucket
    )
  } catch (err) {
    console.error('[analytics] clarity set failed:', err)
  }
}

export function trackPurchase(params: {
  transactionId: string
  value: number
  items: GA4EcommerceItem[]
  currency?: string
}): void {
  trackEcommerce('purchase', {
    transaction_id: params.transactionId,
    value: params.value,
    currency: params.currency ?? DEFAULT_CURRENCY,
    items: params.items,
  })
}

